import React, { useState } from "react";
import axios from "axios";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaRegFile, FaCloudDownloadAlt } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";

const InvoiceAnalysisTool = () => {
  const [files, setFiles] = useState([]);
  const [extractedData, setExtractedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setFiles(selectedFiles);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };

  const extractData = async () => {
    if (files.length === 0) {
      toast.error("Please upload at least one file.");
      return;
    }

    setLoading(true);
    setProgress(0);

    let formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const interval = setInterval(() => {
        setProgress((prev) => (prev < 95 ? prev + 2 : prev));
      }, 200);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}extract-invoice`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      clearInterval(interval);
      setProgress(100);
      setTimeout(() => setProgress(0), 1000);

      setExtractedData(response.data.data);
      toast.success("Extraction completed successfully!");
    } catch (err) {
      toast.error("Failed to extract invoice data. Please try again.");
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    if (extractedData.length === 0) {
      toast.error("No data available to export.");
      return;
    }

    setIsDownloading(true); // Show "Downloading CSV..."

    const headers = [
      "Invoice Number",
      "Invoice Date",
      "Due Date",
      "Vendor Name",
      "Vendor Email",
      "Vendor Phone",
      "Vendor Website",
      "Ship To",
      "Bill To",
      "Customer Email",
      "Customer Phone",
      "Subtotal",
      "Sales Tax",
      "Tax Percentage",
      "Total Amount",
      "Payments Credits",
      "Balance Due",
      "Currency",
      "Tax Amount",
    ];

    const csvRows = [];
    csvRows.push(headers.join(",")); // Add headers

    extractedData.forEach((item) => {
      if (!item.invoice_data) return; // Skip invalid data
      const row = [
        `"${item.invoice_data?.InvoiceNumber || ""}"`,
        `"${item.invoice_data?.InvoiceDate || ""}"`,
        `"${item.invoice_data?.DueDate || ""}"`,
        `"${item.invoice_data?.VendorName || ""}"`,
        `"${item.invoice_data?.VendorEmail || ""}"`,
        `"${item.invoice_data?.VendorPhone || ""}"`,
        `"${item.invoice_data?.VendorWebsite || ""}"`,
        `"${item.invoice_data?.ShipTo || ""}"`,
        `"${item.invoice_data?.BillTo || ""}"`,
        `"${item.invoice_data?.CustomerEmail || ""}"`,
        `"${item.invoice_data?.CustomerPhone || ""}"`,
        `"${item.invoice_data?.Subtotal || ""}"`,
        `"${item.invoice_data?.SalesTax || ""}"`,
        `"${item.invoice_data?.TaxPercentage || ""}"`,
        `"${item.invoice_data?.TotalAmount || ""}"`,
        `"${item.invoice_data?.PaymentsCredits || ""}"`,
        `"${item.invoice_data?.BalanceDue || ""}"`,
        `"${item.invoice_data?.Currency || ""}"`,
        `"${item.invoice_data?.TaxAmount || ""}"`,
      ];
      csvRows.push(row.join(","));
    });

    // Ensure correct encoding for special characters
    const csvContent = "\uFEFF" + csvRows.join("\n"); // Add BOM (Byte Order Mark) for UTF-8 encoding
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", "invoice_data.csv");
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setTimeout(() => {
      setIsDownloading(false); // Reset button text after download
      toast.success("CSV has been exported!");
    }, 1000);
  };

  return (
    <>
      <ToastContainer position="top-center" autoClose={3000} />
      <div className="container mt-5 mb-5">
        <h2 className="fw-bold">Invoice Analysis Tool</h2>
        <p>Upload your invoices (can upload multiple files at once)</p>
       
        <div
          className="border p-4 rounded bg-light text-center"
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
         <div className="row sample_box">
            <div className="col-lg-4">
              <div className="card mb-3">
                <Link target="_blank" rel="noopener noreferrer" to="/sample-invoice.pdf">Sample Invoice 1</Link>
              </div>
            </div>
            <div className="col-lg-4  mb-3">
              <div className="card">
                <Link target="_blank" rel="noopener noreferrer" to="/sample-invoice2.pdf">Sample Invoice 2</Link>
              </div>
            </div>
            <div className="col-lg-4  mb-3">
              <div className="card">
                <Link target="_blank" rel="noopener noreferrer" to="/sample-invoice3.png">Sample Invoice 3</Link>
              </div>
            </div>
          </div>
          <input
            type="file"
            multiple
            className="d-none"
            id="fileInput"
            onChange={handleFileChange}
          />
          <label
            htmlFor="fileInput"
            className="d-block border p-3 rounded bg-white text-muted"
            style={{ cursor: "pointer" }}
          >
            <FaCloudDownloadAlt style={{ fontSize: "40px" }} />
            <div>
              <span className="fw-bold">Drag and drop files here</span>
              <br />
              <small>Limit 200MB per file • PDF, JPG, JPEG, PNG</small>
            </div>
          </label>
          <button
            className="btn btn-outline-dark mt-3"
            onClick={() => document.getElementById("fileInput").click()}
          >
            Browse files
          </button>
        </div>

        {files.length > 0 && (
          <div className="mt-3 border p-3 rounded bg-light">
            {files.map((file, index) => (
              <div
                key={index}
                className="d-flex justify-content-between align-items-center border p-2 rounded mb-2 bg-white"
              >
                <span>
                  <FaRegFile style={{ fontSize: "25px", margin: "15px" }} />
                  {file.name}{" "}
                  <small className="text-muted">
                    ({(file.size / 1024).toFixed(1)} KB)
                  </small>
                </span>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => removeFile(index)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        {files.length > 0 && (
          <button
            className="btn btn-outline-dark mt-3"
            onClick={extractData}
            disabled={loading}
          >
            {loading ? "Extracting..." : "Extract Invoice Data"}
          </button>
        )}
        {loading && (
          <div className="mt-3">
            <div className="progress" style={{ height: "5px" }}>
              <div
                className="progress-bar bg-danger bg-gradient"
                role="progressbar"
                style={{
                  width: `${progress}%`,
                  transition: "width 0.3s ease-in-out",
                }}
              ></div>
            </div>
          </div>
        )}

        {extractedData.length > 0 && (
          <>
            <div className="table-responsive">
              <table className="table table-striped table-bordered mt-3">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Invoice Number</th>
                    <th>Invoice Date</th>
                    <th>Due Date</th>
                    <th>Vendor Name</th>
                    <th>Vendor Email</th>
                    <th>Vendor Phone</th>
                    <th>Vendor Website</th>
                    <th>Ship To</th>
                    <th>Bill To</th>
                    <th>Customer Email</th>
                    <th>Customer Phone</th>
                    <th>Subtotal</th>
                    <th>Sales Tax</th>
                    <th>Tax Percentage</th>
                    <th>Total Amount</th>
                    <th>Payments Credits</th>
                    <th>Balance Due</th>
                    <th>Currency</th>
                    <th>Tax Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {extractedData.map((item, index) => (
                    <tr key={index}>
                      <td className="align-middle">{index + 1}</td>
                      <td className="align-middle">
                        {item.invoice_data.InvoiceNumber}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.InvoiceDate}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.DueDate}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.VendorName}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.VendorEmail}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.VendorPhone}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.VendorWebsite}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.ShipTo}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.BillTo}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.CustomerEmail}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.CustomerPhone}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.Subtotal}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.SalesTax}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.TaxPercentage}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.TotalAmount}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.PaymentsCredits}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.BalanceDue}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.Currency}
                      </td>
                      <td className="align-middle">
                        {item.invoice_data.TaxAmount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {extractedData.length > 0 && (
          <button
            className="btn btn-outline-dark mt-3"
            onClick={exportToCSV}
            disabled={isDownloading}
          >
            {isDownloading ? "Downloading CSV..." : "Download CSV"}
          </button>
        )}
      </div>

    </>
  );
};

export default InvoiceAnalysisTool;
